import axios from 'axios';

import { getToken } from '../services/authentication';

// Dashboard chama ocpp direto (padrão da casa — ver services/request.js).
// REACT_APP_API_URL aponta pra http://localhost:8080/api/v1/ em dev,
// e pra o endpoint ocpp em prod.
// X-Timezone-Offset = horas de GMT (ex: SP = -3). Backend usa pra ajustar datas
// que dependem do fuso do operador (igual financial, smartcharging, virtualgroup).
const timezoneOffsetHours = -(new Date().getTimezoneOffset() / 60);
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    Platform: 'DASHBOARD',
    'X-Timezone-Offset': timezoneOffsetHours,
  },
});

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = token as string;
  }
  return config;
});

export interface ReconciliationTotals {
  candidates: number;
  reconciled: number;
  divergence: number;
  unreconciled: number;
  ambiguous: number;
  /** MV2-1909: breakdown por forma de pagamento (card_pix inclui PAGBANK_CARD e PAGBANK_PIX). */
  wallet?: number;
  card_pix?: number;
}

export interface ReconciliationWarning {
  type: string;
  code?: string;
  file?: string;
  transaction_pk?: number;
  detail?: string;
  affected_transaction_pks?: number[];
  /**
   * MV2-1901 review A1: projeção estruturada de valores (expected, sent, received,
   * refunded, gross) populada para INTERNAL_SPLIT_MISMATCH e PAGBANK_RECEIPT_MISMATCH.
   * Preferir sobre regex em `detail`. Pode ser undefined para warnings legados ou
   * backends mais antigos.
   */
  fields?: Record<string, number | string | null>;
}

/**
 * MV2-1909: payload de 1 tx — populado só no modo view pra tela Histórico abrir detalhe.
 */
export interface ReconciliationDetail {
  transaction_pk: number;
  customer_name: string | null;
  payment_type: string | null;         // PAGBANK_CARD / PAGBANK_PIX / WALLET
  payment_external_id: string | null;
  start_timestamp: string | null;      // ISO datetime
  paid_timestamp: string | null;
  value_brl: number | null;
  refunded_brl: number | null;
  split_percentage: string | null;     // vem como BigDecimal → string
  expected_amount: string | null;
  found_amount: string | null;
  auto_status: string | null;          // RECONCILED/DIVERGENCE/UNRECONCILED/AMBIGUOUS
  effective_status: string | null;     // auto ou RECONCILED_MANUAL
  auto_reconciled_at: string | null;
  manual_reason: string | null;
  manual_by_user_name: string | null;
  manual_at: string | null;
}

export interface ReconciliationUploadResponse {
  reference_period: string;
  totals: ReconciliationTotals;
  warnings: ReconciliationWarning[];
  audit_pk: string | null;
  /** Preenchido só em modo view (GET /reconciliations/view). */
  details?: ReconciliationDetail[];
}

/**
 * Upload do extrato PagBank + relatório de vendas pra processar reconciliação de uma competência.
 * Backend: ocpp POST /api/v1/reconciliations/upload (MV2-1902).
 * Period format: 'YYYY-MM' (ex: '2026-05').
 */
export const uploadReconciliation = async (
  extrato: File,
  vendas: File,
  referencePeriod: string,
): Promise<ReconciliationUploadResponse> => {
  const formData = new FormData();
  formData.append('extrato', extrato);
  formData.append('vendas', vendas);
  formData.append('reference_period', referencePeriod);

  const response = await api.post<ReconciliationUploadResponse>(
    '/reconciliations/upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data;
};

/**
 * Status efetivo exibido na UI, derivado dos campos retornados pelo financial
 * (effectiveStatus do backend). Mapeado pros labels pt-br e cores do Tag antd.
 */
export const RECONCILIATION_STATUS_META: Record<
  string,
  { label: string; color: string }
> = {
  RECONCILED: { label: 'Conciliado', color: 'green' },
  RECONCILED_MANUAL: { label: 'Conciliado (Manual)', color: 'blue' },
  DIVERGENCE: { label: 'Divergência', color: 'red' },
  UNRECONCILED: { label: 'Não conciliado', color: 'gold' },
  AMBIGUOUS: { label: 'Ambíguo', color: 'volcano' },
};

/** Status onde a ação "Conciliar manualmente" é permitida (reflete allowlist do backend MV2-1907). */
export const MANUAL_RECONCILE_ALLOWED_STATUSES = new Set<string>([
  'DIVERGENCE',
  'UNRECONCILED',
  'AMBIGUOUS',
]);

export interface ManualReconcileResponse {
  transaction_pk: number;
  reference_period: string;
  effective_status: string;
  auto_status: string | null;
  manual_reconciled_by_user_pk: number | null;
  manual_reconciled_at: string | null;
  manual_reason: string | null;
}

/**
 * Marca uma linha de reconciliação como conciliada manualmente (MV2-1907).
 * referencePeriod formato 'YYYY-MM'.
 */
export const manualReconcile = async (
  transactionPk: number,
  referencePeriod: string,
  reason: string,
): Promise<ManualReconcileResponse> => {
  const response = await api.post<ManualReconcileResponse>(
    `/reconciliations/${transactionPk}/manual-reconcile`,
    { reference_period: referencePeriod, reason },
  );
  return response.data;
};

/**
 * Desfaz uma conciliação manual, restaurando o status automático original (MV2-1907).
 */
export const manualUndo = async (
  transactionPk: number,
  referencePeriod: string,
): Promise<ManualReconcileResponse> => {
  const response = await api.post<ManualReconcileResponse>(
    `/reconciliations/${transactionPk}/manual-undo`,
    { reference_period: referencePeriod },
  );
  return response.data;
};

export interface ReprocessResult {
  transactionPk: number;
  referencePeriod: string;
  newAutoStatus: string;
  changed: boolean;
}

export interface ReprocessBatchResult {
  requested: number;
  nowReconciled: number;
  stillUnreconciled: number;
  skipped: number;
}

/**
 * MV2-1908: reprocessa uma tx UNRECONCILED específica. Re-consulta PagBank;
 * se agora encontrou split/transfer, backend promove pra RECONCILED.
 */
export const reprocessReconciliation = async (
  transactionPk: number,
  referencePeriod: string,
): Promise<ReprocessResult> => {
  const response = await api.post<ReprocessResult>(
    `/reconciliations/${transactionPk}/reprocess`,
    { reference_period: referencePeriod },
  );
  return response.data;
};

/**
 * MV2-1908: reprocessa várias tx em lote. Pula silenciosamente as inelegíveis
 * (não UNRECONCILED ou manuais).
 */
export const reprocessReconciliationBatch = async (
  transactionPks: number[],
  referencePeriod: string,
): Promise<ReprocessBatchResult> => {
  const response = await api.post<ReprocessBatchResult>(
    '/reconciliations/reprocess-batch',
    { reference_period: referencePeriod, transaction_pks: transactionPks },
  );
  return response.data;
};

/**
 * MV2-1909: entrada na tela Histórico de conciliações — uma por competência processada.
 * Backend retorna snake_case; aqui mapeamos pra camelCase.
 */
export interface ReconciliationHistoryEntry {
  referencePeriod: string;         // "YYYY-MM"
  lastProcessedAt: string | null;  // ISO datetime
  lastActorUserPk: number | null;
  lastActorName: string | null;
  lastActorEmail: string | null;
  total: number;
  reconciled: number;
  divergence: number;
  unreconciled: number;
  fullyReconciled: boolean;
}

export interface ReconciliationHistoryPage {
  page: number;
  size: number;
  total: number;
  entries: ReconciliationHistoryEntry[];
}

export const getReconciliationHistory = async (
  page: number = 0,
  size: number = 20,
  from?: string | null,
  to?: string | null,
): Promise<ReconciliationHistoryPage> => {
  const params: Record<string, unknown> = { page, size };
  if (from) params.from = from;
  if (to) params.to = to;
  const response = await api.get<any>('/reconciliations/history', { params });
  const data = response.data || {};
  return {
    page: data.page ?? 0,
    size: data.size ?? size,
    total: data.total ?? 0,
    // MV2-1901 review CR-A3: HistoryEntry no backend não usa @JsonProperty, portanto
    // sai camelCase direto (default Jackson). Fallback snake_case removido — era
    // dead code que mascarava a inconsistência real do contrato.
    entries: (data.entries || []).map((r: any) => ({
      referencePeriod: r.referencePeriod,
      lastProcessedAt: r.lastProcessedAt,
      lastActorUserPk: r.lastActorUserPk,
      lastActorName: r.lastActorName,
      lastActorEmail: r.lastActorEmail,
      total: r.total,
      reconciled: r.reconciled,
      divergence: r.divergence,
      unreconciled: r.unreconciled,
      fullyReconciled: r.fullyReconciled,
    })),
  };
};

/**
 * MV2-1909: estado atual da conciliação duma competência pra popular o modal em modo view.
 * Mesma shape da ReconciliationUploadResponse — reusa a UI do modal pós-upload.
 */
export const getReconciliationView = async (
  referencePeriod: string,
): Promise<ReconciliationUploadResponse> => {
  const response = await api.get<ReconciliationUploadResponse>('/reconciliations/view', {
    params: { period: referencePeriod },
  });
  return response.data;
};

export type ReconciliationReportFormat = 'xlsx' | 'pdf';

const REPORT_MIME: Record<ReconciliationReportFormat, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
};

/**
 * Baixa o relatório formatado da reconciliação (MV2-1909). Fonte da verdade imediata do ocpp.
 * XLSX pra análise financeira, PDF pra comprovante formal. Dispara download via Blob.
 */
export const downloadReconciliationReport = async (
  referencePeriod: string,
  format: ReconciliationReportFormat = 'xlsx',
): Promise<void> => {
  const response = await api.get(`/reconciliations/report.${format}`, {
    params: { period: referencePeriod },
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: REPORT_MIME[format] });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `conciliacao-${referencePeriod}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
